import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Chart from "chart.js/auto";
import "./ResultsPersonality.css";

const traits = [
  "Extraversion",
  "Agreeableness",
  "Conscientiousness",
  "Emotional Stability",
  "Intellect/Imagination",
];

const traitDefinitions = {
  Extraversion: "The tendency to be outgoing, energetic, and sociable.",
  Agreeableness: "The tendency to be compassionate, cooperative, and trusting.",
  Conscientiousness:
    "The tendency to be organized, responsible, and hardworking.",
  "Emotional Stability":
    "The ability to remain calm and composed under stress.",
  "Intellect/Imagination":
    "The tendency to be open to new experiences and ideas.",
};

function ResultsPersonality() {
  const [score, setScore] = useState(null);
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const navigate = useNavigate();

  // Fetch score from backend
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_FLASK_API_URL}/score/personality`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setScore(data))
      .catch((err) =>
        console.error("Error fetching personality results:", err)
      );
  }, []);

  // Chart rendering
  useEffect(() => {
    if (!score || !score.score || !score.maxScore) return;

    const percentages = traits.map((t) =>
      Math.round((score.score[t] / score.maxScore[t]) * 100)
    );

    const data = {
      labels: traits,
      datasets: [
        {
          label: "Personality Traits",
          data: percentages,
          backgroundColor: "rgba(153, 102, 255, 0.2)",
          borderColor: "rgb(153, 102, 255)",
          borderWidth: 2,
          fill: true,
        },
      ],
    };

    const config = {
      type: "radar",
      data,
      options: {
        responsive: true,
        scales: {
          r: { suggestedMin: 0, suggestedMax: 100 },
        },
      },
    };

    if (chartRef.current) chartRef.current.destroy();
    chartRef.current = new Chart(canvasRef.current, config);
  }, [score]);

  const handleLogout = () => {
    fetch(`${process.env.REACT_APP_BACKEND_FLASK_API_URL}/logout`, {
      method: "POST",
      credentials: "include",
    })
      .then((res) => res.json())
      .then(() => navigate("/"))
      .catch((err) => console.error("Logout error:", err));
  };

  if (!score) {
    return <div>Calculating Personality Results...</div>;
  }

  return (
    <div className="results-container">
      <header className="dash-header">
        <button onClick={() => navigate("/dashboard")}>Dashboard</button>
        <button onClick={() => navigate("/quiz-nav")}>Quizzes</button>
        <button onClick={handleLogout}>Log Out</button>
      </header>

      <h2>Your Personality Results</h2>

      <div className="results-flex">
        {/* Chart */}
        <div className="results-chart">
          <canvas ref={canvasRef}></canvas>
        </div>

        {/* Score Description */}
        <div className="results-details">
          {traits.map((t, i) => (
            <p key={i}>
              <b>{t}:</b> {score.score[t]} / {score.maxScore[t]} (
              {Math.round((score.score[t] / score.maxScore[t]) * 100)}%)
              <br />
              <span className="trait-definition">{traitDefinitions[t]}</span>
            </p>
          ))}
        </div>
      </div>

      <p className="disclaimer">
        <b>Disclaimer:</b> These results are based on your responses to the
        personality scale. They provide insight into your traits but are not a
        fixed definition of who you are. Personality is dynamic and can change
        over time. Use these insights for reflection and self-growth.
      </p>
    </div>
  );
}

export default ResultsPersonality;
